import { useRef, useState } from 'react';
import { Button, Group, Stack, Switch, TextInput } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError } from 'src/error/ApiError';
import useSaveCustomSkill from './hooks/useSaveCustomSkill';
import type { CustomSkill } from './types';

const NAME_PATTERN = /^[a-z][a-z0-9_]{2,49}$/;

type CustomSkillEditorProps = {
  companyId: string;
  skill: CustomSkill | null;
  onClose: () => void;
};

export default function CustomSkillEditor({ companyId, skill, onClose }: CustomSkillEditorProps) {
  const [name, setName] = useState(skill?.name ?? '');
  const [description, setDescription] = useState(skill?.description ?? '');
  const [requiresSandbox, setRequiresSandbox] = useState(skill?.requiresSandbox ?? false);
  const [fileExplorerEnabled, setFileExplorerEnabled] = useState(
    skill?.fileExplorerEnabled ?? false,
  );
  const [companyFilesVisible, setCompanyFilesVisible] = useState(
    skill?.companyFilesVisible ?? false,
  );
  const [isEnabled, setIsEnabled] = useState(skill?.isEnabled ?? true);
  const [hasMarkdownContent, setHasMarkdownContent] = useState(
    (skill?.markdown ?? '').trim().length > 0,
  );
  const markdownRef = useRef(skill?.markdown ?? '');

  const { mutateAsync: saveSkill, isPending } = useSaveCustomSkill(companyId);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isNameValid = NAME_PATTERN.test(trimmedName);
  const canSave = isNameValid && trimmedDescription.length > 0 && hasMarkdownContent;

  const nameError =
    trimmedName.length > 0 && !isNameValid
      ? 'Use snake_case: lowercase letters, digits and underscores, starting with a letter (3 to 50 characters).'
      : null;

  // Capability hierarchy: company files ⟹ file explorer ⟹ sandbox. Enabling a deeper capability
  // switches on the shallower ones; a sandbox on its own implies nothing further.
  const effectiveFileExplorer = fileExplorerEnabled || companyFilesVisible;
  const effectiveSandbox = requiresSandbox || effectiveFileExplorer;

  const handleSave = async () => {
    if (!canSave) return;

    try {
      await saveSkill({
        id: skill?.id,
        name: trimmedName,
        description: trimmedDescription,
        markdown: markdownRef.current,
        requiresSandbox: effectiveSandbox,
        fileExplorerEnabled: effectiveFileExplorer,
        companyFilesVisible,
        isEnabled,
      });
      onClose();
    } catch (error) {
      const detail =
        error instanceof ApiError ? error.message : 'An error occurred while saving the skill.';
      notifyAndLogError('Failed to save skill', detail, error);
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Name"
        description="Identifier the AI uses to activate the skill (snake_case, e.g. firm_report_template)"
        placeholder="firm_report_template"
        value={name}
        onChange={e => setName(e.currentTarget.value)}
        error={nameError}
        required
      />
      <TextInput
        label="Description"
        description="Short summary shown to the AI alongside other available skills"
        placeholder="Generate a firm-branded suitability report"
        value={description}
        onChange={e => setDescription(e.currentTarget.value)}
        required
      />
      <Switch
        label="Requires sandbox"
        description="Enable if the skill instructions tell the AI to create or edit files. Switched on automatically when file explorer tools or company files are enabled."
        checked={effectiveSandbox}
        disabled={effectiveFileExplorer}
        onChange={e => setRequiresSandbox(e.currentTarget.checked)}
      />
      <Switch
        label="Provide file explorer tools"
        description="Give the skill the file explorer tools (to_markdown, query_file) to read the client's uploaded files. Requires the sandbox, so it switches that on too."
        checked={effectiveFileExplorer}
        disabled={companyFilesVisible}
        onChange={e => setFileExplorerEnabled(e.currentTarget.checked)}
      />
      <Switch
        label="Make company files visible to skill"
        description="Seed the company file store (read-only) into the sandbox so this skill can read company documents. Switches on the file explorer tools and the sandbox."
        checked={companyFilesVisible}
        onChange={e => setCompanyFilesVisible(e.currentTarget.checked)}
      />
      <Switch
        label="Enabled"
        description="Disabled skills are hidden from chat without being deleted"
        checked={isEnabled}
        onChange={e => setIsEnabled(e.currentTarget.checked)}
      />
      <MarkdownEditor
        defaultValue={markdownRef.current}
        onChange={value => {
          markdownRef.current = value;
          setHasMarkdownContent(value.trim().length > 0);
        }}
        label="Skill definition"
        rootClass="compact"
      />
      <Group>
        <Button size="xs" onClick={handleSave} loading={isPending} disabled={!canSave}>
          Save
        </Button>
        <Button size="xs" variant="subtle" onClick={onClose}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}
